import { useState, useEffect, useCallback } from "react"
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import { Title } from "components/Common/Title"
import { ExamSectionSkeleton } from "./ExamSectionSkeleton"
import analyticsService from "services/analyticsService"
import { fmtMetric } from "./analyticsFormatters"
import type { IAnalyticsExamTaskLesson } from "types/analyticsTypes"

type TaskRowProps = {
  row: IAnalyticsExamTaskLesson
}

function TaskRow({ row }: TaskRowProps) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen((prev) => !prev)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{row.lessonName ?? `Unknown (${row.lessonId})`}</TableCell>
        <TableCell align="right">{row.sessionCount}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={3} sx={{ py: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Average Task Metrics
              </Typography>
              {Object.keys(row.metrics).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No task metrics recorded.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>Metric</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(row.metrics).map(([key, value]) => (
                      <TableRow key={key}>
                        <TableCell>{key}</TableCell>
                        <TableCell align="right">{fmtMetric(value)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

type Props = {
  orgId: string | null
}

export function ExamTasksSection({ orgId }: Props) {
  const [data, setData] = useState<IAnalyticsExamTaskLesson[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(() => {
    setLoading(true)
    setError(null)
    analyticsService
      .getExamsTasks(orgId)
      .then((result) => {
        setData(result)
        setLoading(false)
      })
      .catch(() => {
        setError("Failed to load exam task data.")
        setLoading(false)
      })
  }, [orgId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  if (loading) return <ExamSectionSkeleton rows={5} />

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Title>AVERAGE EXAM TASK METRICS BY LESSON</Title>
        <Typography color="error" variant="body2">
          {error}
        </Typography>
        <Button size="small" onClick={fetchData} sx={{ mt: 1 }}>
          Retry
        </Button>
      </Paper>
    )
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Title>AVERAGE EXAM TASK METRICS BY LESSON</Title>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Expand a lesson to see average per-task metric values across all exam sessions.
      </Typography>

      {data.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No exam task data available.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell sx={{ fontWeight: "bold" }}>Lesson</TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold" }}>Sessions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row) => (
                <TaskRow key={row.lessonId} row={row} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  )
}
